import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Link2, Loader2, Check, RefreshCw, AlertTriangle, KeyRound } from 'lucide-react';
import FieldHelp from '@/components/help/FieldHelp';

/**
 * Meta Fan Page connect — selecciona QUÉ página de Facebook (y su Instagram
 * ligado, si tiene) va a operar CavernicolApp. Reutiliza la conexión de
 * cuenta de Meta que el usuario ya haya hecho en Ajustes > Conexiones (o la
 * inicia aquí mismo si no existe todavía).
 * @param {object} props
 * @param {function} props.onConnected — callback after a page is connected
 * @param {string} [props.branchId] — optional branch scope
 * @param {boolean} [props.compact] — inline vs full panel
 */
export default function MetaFanPageConnect({ onConnected, branchId = '', compact = false }) {
  const [status, setStatus] = useState('loading'); // loading | need_account | need_pages_scope | ready | error
  const [pages, setPages] = useState([]);
  const [connecting, setConnecting] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [togglingAi, setTogglingAi] = useState(false);
  const [webhookSubscribed, setWebhookSubscribed] = useState(null);
  const [leadgenSubscribed, setLeadgenSubscribed] = useState(null);
  const [resubscribing, setResubscribing] = useState(false);
  const [pageFullyConnected, setPageFullyConnected] = useState(false);
  const [pagesEmptyReason, setPagesEmptyReason] = useState(null);
  const [connectMethod, setConnectMethod] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manualPageId, setManualPageId] = useState('');
  const [manualPageToken, setManualPageToken] = useState('');
  const [manualPageName, setManualPageName] = useState('');
  const [manualConnecting, setManualConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [accountConnected, setAccountConnected] = useState(false);
  const [accountEmail, setAccountEmail] = useState(null);
  const [accountIdMasked, setAccountIdMasked] = useState(null);
  const [permissions, setPermissions] = useState(null);

  const loadPages = useCallback(async () => {
    setStatus('loading');
    try {
      const [acctStatus, pagesResp] = await Promise.all([
        api.get('/tenant/oauth/meta/status').catch(() => ({ data: { connected: false } })),
        api.get('/tenant/oauth/meta/pages'),
      ]);
      const { data } = pagesResp;
      const isAccountConnected = !!acctStatus.data?.connected;
      const fullyConnected = !!data.page_fully_connected;

      setPages(data.pages || []);
      setAiEnabled(data.messenger_ai_enabled !== false);
      setWebhookSubscribed(data.webhook_subscribed);
      setLeadgenSubscribed(data.leadgen_subscribed ?? null);
      setPageFullyConnected(fullyConnected);
      setPagesEmptyReason(data.pages_empty_reason || null);
      setConnectMethod(data.connect_method || null);
      setAccountConnected(isAccountConnected);
      setAccountEmail(acctStatus.data?.email || null);
      setAccountIdMasked(acctStatus.data?.external_account_id_masked || null);
      setPermissions(data.permissions || null);

      if (fullyConnected || (data.pages || []).length > 0) {
        setStatus('ready');
      } else if (!isAccountConnected) {
        setStatus('need_account');
      } else {
        setStatus('need_pages_scope');
        if (data.pages_empty_reason === 'no_page_admin_role') {
          setShowManual(true);
        }
      }
    } catch (e) {
      if (e.response?.data?.detail?.code === 'meta_not_connected') {
        setStatus('need_account');
      } else {
        setStatus('error');
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
  }, []);

  useEffect(() => { loadPages(); }, [loadPages]);

  // Vuelve del redirect de Meta con ?oauth=meta&connected=1 en la URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') === 'meta') {
      loadPages();
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth'); url.searchParams.delete('connected'); url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectAccount = async () => {
    setRedirecting(true);
    try {
      const { data } = await api.get('/tenant/oauth/meta/authorize', {
        params: { return_to: window.location.pathname },
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo iniciar la conexión con Meta');
      setRedirecting(false);
    }
  };

  const reconnectAccount = async () => {
    setReconnecting(true);
    try {
      await api.post('/tenant/oauth/meta/disconnect').catch(() => null);
      await connectAccount();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo reiniciar la conexión con Meta');
      setReconnecting(false);
    }
  };

  // Reautorizar = volver a pedirle permisos a Meta SIN tocar la Fan Page ya
  // conectada (a diferencia de reconnectAccount, que primero desconecta).
  // El backend ya arma la URL con auth_type=rerequest, así que reusar
  // connectAccount tal cual es exactamente lo que se necesita aquí.
  const reauthorizeAccount = connectAccount;

  const disconnectPage = async () => {
    if (!window.confirm(
      'Desconectar la Fan Page de este negocio? Queda libre para otro negocio.'
    )) return;
    setDisconnecting(true);
    try {
      await api.post('/tenant/oauth/meta/pages/disconnect', null, {
        params: branchId ? { branch_id: branchId } : undefined,
      });
      toast.success('Fan Page desconectada — ya puedes usarla en otro negocio');
      await loadPages();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo desconectar la Fan Page');
    } finally {
      setDisconnecting(false);
    }
  };

  const disconnectAccount = async () => {
    if (!window.confirm(
      'Desconectar Meta de este negocio? Tambien libera la Fan Page para moverla a otro negocio.'
    )) return;
    setDisconnecting(true);
    try {
      await api.post('/tenant/oauth/meta/pages/disconnect', null, {
        params: branchId ? { branch_id: branchId } : undefined,
      }).catch(() => null);
      await api.post('/tenant/oauth/meta/disconnect');
      toast.success('Meta y Fan Page desconectados de este negocio');
      await loadPages();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo desconectar');
    } finally {
      setDisconnecting(false);
    }
  };

  const MISSING_SCOPE_LABELS = {
    pages_manage_engagement: 'Responder comentarios del muro de Facebook',
    pages_show_list: 'Ver la lista de tus páginas',
    pages_read_engagement: 'Leer comentarios del muro',
    pages_read_user_content: 'Leer contenido de usuarios en comentarios',
    pages_manage_metadata: 'Suscribir la página a webhooks',
    pages_messaging: 'Enviar/recibir Messenger',
    pages_manage_posts: 'Publicar en el muro de la página (Social & Ads)',
    leads_retrieval: 'Leer formularios de Meta Ads (Leads)',
    business_management: 'Administrar cuentas publicitarias',
    ads_management: 'Crear/editar campañas de Meta Ads',
    ads_read: 'Leer métricas de Meta Ads',
  };

  const connectPage = async (page) => {
    setConnecting(page.page_id);
    try {
      const { data } = await api.post(`/tenant/oauth/meta/pages/${page.page_id}/connect`, {
        page_name: page.page_name,
        instagram_business_account_id: page.instagram_business_account_id,
        instagram_username: page.instagram_username,
        branch_id: branchId || null,
      });
      if (data.webhook_subscribed === false) {
        toast.warning(`Página "${page.page_name}" conectada, pero Meta rechazó la suscripción a mensajes/comentarios — vuelve a intentar en unos minutos, o avisa a soporte si persiste.`);
      } else {
        toast.success(`Página "${page.page_name}" conectada`);
      }
      await loadPages();
      onConnected?.(page);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConnecting(null);
    }
  };

  const connectManual = async () => {
    if (!manualPageId.trim() || !manualPageToken.trim()) {
      toast.error('Page ID y Page Access Token son obligatorios');
      return;
    }
    setManualConnecting(true);
    try {
      const { data } = await api.post('/tenant/oauth/meta/pages/manual-connect', {
        page_id: manualPageId.trim(),
        page_access_token: manualPageToken.trim(),
        page_name: manualPageName.trim() || null,
        branch_id: branchId || null,
      });
      if (data.webhook_subscribed === false) {
        toast.warning(`Página "${data.page_name}" conectada manualmente, pero Meta rechazó la suscripción a webhooks — reintenta en unos minutos.`);
      } else {
        toast.success(`Página "${data.page_name}" conectada manualmente`);
      }
      setManualPageToken('');
      await loadPages();
      onConnected?.({ page_id: manualPageId.trim(), page_name: data.page_name });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setManualConnecting(false);
    }
  };

  const toggleAi = async (checked) => {
    setTogglingAi(true);
    try {
      await api.post('/tenant/oauth/meta/pages/ai-toggle', { enabled: checked, branch_id: branchId || null });
      setAiEnabled(checked);
      toast.success(checked ? 'La IA va a responder Messenger automáticamente' : 'La IA ya no responde Messenger automáticamente');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTogglingAi(false);
    }
  };

  const manualConnectPanel = (
    <div className="mt-4 border border-[#E2E8F0] rounded-lg p-4 bg-[#F8FAFC] space-y-3" data-testid="meta-page-manual-connect">
      <div className="flex items-center justify-between gap-2">
        <FieldHelp
          label="Conexión manual (Page Access Token)"
          help="Alternativa cuando OAuth no lista tu Fan Page: pega el Page ID y un Page Access Token de larga duración desde Meta Business Suite → Configuración → Páginas → Tu página → Token de acceso. No requiere ser admin directo en el perfil personal de Facebook."
        >
          <p className="text-xs text-[#64748B]">
            Usa esto si Meta no muestra tu página en la lista pero tienes el token de la Página.
          </p>
        </FieldHelp>
        {(status === 'ready' || status === 'need_pages_scope') && (
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs shrink-0" onClick={() => setShowManual(v => !v)}>
            {showManual ? 'Ocultar' : 'Mostrar'}
          </Button>
        )}
      </div>
      {(showManual || status === 'need_account') && (
        <div className="space-y-2">
          <Input
            placeholder="Page ID (ej. 123456789012345)"
            value={manualPageId}
            onChange={(e) => setManualPageId(e.target.value)}
            data-testid="meta-manual-page-id"
          />
          <Input
            type="password"
            placeholder="Page Access Token (long-lived)"
            value={manualPageToken}
            onChange={(e) => setManualPageToken(e.target.value)}
            data-testid="meta-manual-page-token"
          />
          <Input
            placeholder="Nombre de la página (opcional)"
            value={manualPageName}
            onChange={(e) => setManualPageName(e.target.value)}
          />
          <Button onClick={connectManual} disabled={manualConnecting} className="gap-2 w-full sm:w-auto">
            {manualConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Conectar con token manual
          </Button>
        </div>
      )}
    </div>
  );

  if (status === 'loading') {
    return <div className="flex items-center gap-2 text-sm text-[#64748B] py-2"><Loader2 className="w-4 h-4 animate-spin" /> Buscando tus páginas de Meta...</div>;
  }

  if (status === 'need_account') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <p className="text-sm text-[#64748B] mb-3">Conecta tu cuenta de Meta para elegir la Fan Page automáticamente, o usa la conexión manual si ya tienes el Page Access Token.</p>
        <Button onClick={connectAccount} disabled={redirecting} className="gap-2 mb-2">
          {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Conectar cuenta de Meta
        </Button>
        {manualConnectPanel}
      </div>
    );
  }

  if (status === 'need_pages_scope') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <p className="text-sm text-[#64748B] mb-3">
          {pagesEmptyReason === 'no_page_admin_role' ? (
            <>
              Tu perfil de Facebook ya autorizó todos los permisos, pero Meta no devuelve ninguna Fan Page para esta cuenta.
              Esto no es un bug de CavernicolApp: tu usuario personal debe ser <strong>administrador directo</strong> de la Página
              (Facebook → Página → Configuración → Acceso a la página, o Business Suite → Personas con acceso).
              Si solo administras vía Business Manager sin rol en la Página, <code>/me/accounts</code> devuelve 0 páginas — usa la conexión manual abajo.
            </>
          ) : (
            <>
              Tu cuenta de Meta está conectada, pero no encontramos páginas — reconecta para autorizar acceso a páginas
              (esto pasa si conectaste antes de que agregáramos esta función o faltan permisos), o conecta manualmente.
            </>
          )}
        </p>
        <Button
          onClick={reconnectAccount}
          disabled={redirecting || reconnecting || disconnecting}
          variant="outline"
          className="gap-2 mb-3"
          data-testid="meta-reconnect-button"
        >
          {(redirecting || reconnecting) ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Desconectar y reconectar cuenta de Meta
        </Button>
        {manualConnectPanel}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <Button onClick={loadPages} variant="outline" size="sm" className="gap-2"><RefreshCw className="w-4 h-4" /> Reintentar</Button>
      </div>
    );
  }

  const hasConnectedPage = pages.some(p => p.connected) || pageFullyConnected;
  const missingScopes = permissions?.missing || [];
  const hasMissingScopes = permissions?.checked && missingScopes.length > 0;

  return (
    <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
      <p className="text-xs text-[#64748B] mb-3">Elige la página que va a atender la IA (Messenger y comentarios del muro de Facebook). Instagram se conecta aparte, más abajo en Integraciones.</p>

      {/* Estado de la cuenta de Meta (login OAuth) — separado del estado de la Fan Page */}
      <div className="flex items-center justify-between gap-3 border border-[#E2E8F0] rounded-lg px-3 py-2 mb-3 bg-[#F8FAFC]" data-testid="meta-account-state">
        <div>
          <p className="text-sm font-medium text-[#0F172A]">
            Cuenta de Meta: {accountConnected ? <span className="text-emerald-600">Conectada</span> : <span className="text-amber-600">Desconectada</span>}
          </p>
          {accountConnected && (accountEmail || accountIdMasked) && (
            <p className="text-[11px] text-[#94A3B8]" data-testid="meta-connected-identity">
              {accountEmail}{accountEmail && accountIdMasked ? ' · ' : ''}{accountIdMasked && `ID ${accountIdMasked}`}
            </p>
          )}
          {accountConnected && (
            <p className="text-[10px] text-amber-600 mt-0.5">
              Verifica que esta sea tu propia cuenta de Meta antes de elegir una página — si ves un nombre que no reconoces, desconecta y vuelve a conectar con la cuenta correcta.
            </p>
          )}
          {!accountConnected && (
            <p className="text-[11px] text-[#94A3B8]">Sin cuenta de Meta no puedes cambiar de pagina. Para moverla a otro negocio usa Desconectar Fan Page.</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {accountConnected ? (
            <>
              <Button
                size="sm" variant="outline" className="gap-2"
                onClick={reauthorizeAccount} disabled={redirecting}
                data-testid="meta-reauthorize-button"
              >
                {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Reautorizar cuenta de Meta
              </Button>
              <Button
                size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={disconnectAccount} disabled={disconnecting}
                data-testid="meta-disconnect-account-button"
              >
                {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desconectar cuenta'}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={connectAccount} disabled={redirecting} className="gap-2" data-testid="meta-connect-account-button">
              {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              Conectar cuenta de Meta
            </Button>
          )}
        </div>
      </div>

      {hasMissingScopes && (
        <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-lg px-3 py-2 mb-3 text-xs space-y-2" data-testid="meta-missing-scopes">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              A tu cuenta de Meta le faltan permisos: {missingScopes.map(s => MISSING_SCOPE_LABELS[s] || s).join(', ')}.
              Pulsa "Reautorizar cuenta de Meta" arriba para concederlos (Meta te mostrará el diálogo de permisos otra vez).
            </span>
          </div>
        </div>
      )}

      {!hasConnectedPage && pages.length > 0 && (
        <div className="flex items-start gap-2 border border-blue-200 bg-blue-50 text-blue-900 rounded-lg px-3 py-2 mb-3 text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Tu cuenta de Meta ya está autorizada — falta el <strong>paso 2</strong>: pulsa <strong>Conectar</strong> en la Fan Page que quieras usar. Sin eso no llegan mensajes de Messenger.</span>
        </div>
      )}
      {hasConnectedPage && webhookSubscribed === false && (
        <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-lg px-3 py-2 mb-3 text-xs space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Meta no confirmó la suscripción a mensajes/comentarios de esta página — no llegarán conversaciones de Messenger hasta resolverlo.</span>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
            try {
              await api.post('/tenant/oauth/meta/pages/resubscribe-webhooks');
              toast.success('Suscripción a webhooks reintentada');
              await loadPages();
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}>Reintentar suscripción</Button>
        </div>
      )}
      {hasConnectedPage && webhookSubscribed !== false && (
        <div className={`border rounded-lg px-3 py-2 mb-3 text-xs space-y-2 ${leadgenSubscribed ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          <div className="flex items-start gap-2">
            {leadgenSubscribed
              ? <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            <span>
              {leadgenSubscribed ? (
                <>Los <strong>formularios de anuncios de Meta</strong> (Lead Ads) entran solos a tu CRM: cada persona que llena el formulario aparece como lead en cuestión de segundos.</>
              ) : (
                <>Esta página aún no recibe los <strong>formularios de anuncios de Meta</strong> (Lead Ads). Los mensajes y comentarios sí funcionan; lo que falta es que Meta autorice el envío de esos formularios. Pulsa el botón para reintentarlo.</>
              )}
            </span>
          </div>
          {!leadgenSubscribed && (
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={resubscribing} onClick={async () => {
              setResubscribing(true);
              try {
                const { data } = await api.post('/tenant/oauth/meta/pages/resubscribe-webhooks');
                if (data?.leadgen_subscribed) {
                  toast.success('Listo: los formularios de anuncios ya llegarán al CRM');
                } else {
                  toast.warning('Meta aceptó mensajes y comentarios, pero todavía no los formularios de anuncios. Suele deberse a que el permiso leads_retrieval sigue en revisión.');
                }
                await loadPages();
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
              } finally {
                setResubscribing(false);
              }
            }}>{resubscribing ? 'Reintentando…' : 'Activar formularios de anuncios'}</Button>
          )}
        </div>
      )}
      {hasConnectedPage && permissions?.checked && (
        <div className="flex items-center gap-2 border border-[#E2E8F0] rounded-lg px-3 py-2 mb-3 bg-[#F8FAFC]" data-testid="meta-can-publish-badge">
          {permissions.can_publish ? (
            <span className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Puede publicar en Social &amp; Ads (Facebook)</span>
          ) : (
            <span className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> No puede publicar en Social &amp; Ads todavía — pulsa "Reautorizar cuenta de Meta" arriba</span>
          )}
        </div>
      )}
      {hasConnectedPage && (
        <div className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 mb-3 bg-[#F8FAFC]">
          <div>
            <p className="text-sm font-medium text-[#0F172A]">La IA responde Messenger automáticamente</p>
            <p className="text-[11px] text-[#94A3B8]">Apágalo si prefieres que tu equipo conteste manualmente por ahora.</p>
          </div>
          <Switch checked={aiEnabled} disabled={togglingAi} onCheckedChange={toggleAi} />
        </div>
      )}
      <div className="space-y-2">
        {pages.map(p => (
          <div key={p.page_id} className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2">
            <div>
              <p className="text-sm font-medium text-[#0F172A]">{p.page_name}</p>
              {p.instagram_username && <p className="text-[11px] text-[#94A3B8]">Instagram ligado: @{p.instagram_username}</p>}
              {p.connect_method === 'manual' && (
                <p className="text-[11px] text-[#64748B]">Conexión manual (Page Access Token)</p>
              )}
            </div>
            {p.connected ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Conectada</span>
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
                  onClick={disconnectPage} disabled={disconnecting} data-testid="meta-disconnect-page-button">
                  {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desconectar Fan Page'}
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => connectPage(p)} disabled={connecting === p.page_id || !!hasConnectedPage}>
                {connecting === p.page_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conectar'}
              </Button>
            )}
          </div>
        ))}
      </div>
      {connectMethod === 'manual' && !showManual && (
        <p className="text-[11px] text-[#94A3B8] mt-2">Fan Page conectada vía token manual. Puedes actualizar el token desde el panel manual abajo.</p>
      )}
      {manualConnectPanel}
    </div>
  );
}